import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

// Import unified types and transformations
import type {
  CallHistoryResponse,
  SmsHistoryResponse,
  BackendCallRecord,
  BackendCallRecordSingle,
  BackendSmsRecord,
  CallHistoryQueryParams,
  SmsHistoryQueryParams,
  CallStatusResponse,
  ApiResponse,
  PaginationMeta,
} from '../types/communication-history.types';

import {
  transformCallHistoryResponse,
  transformSmsHistoryResponse,
  transformCallRecord,
} from '../utils/transform-communication-data';

export const communicationService = {
  // Fetch call history
  getCalls: async (params?: CallHistoryQueryParams) => {
    try {
      const queryParams = new URLSearchParams();

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const url = params && Object.keys(params).length > 0
        ? `${getApiUrl(endpoint.communication.getCalls)}?${queryParams.toString()}`
        : getApiUrl(endpoint.communication.getCalls);

      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: true
        });
        return response;
      });

      // Type-safe response handling
      if (result && Array.isArray((result as CallHistoryResponse).items)) {
        return result as CallHistoryResponse;
      } else {
        // Fallback: return empty structure if data is missing

        const emptyResponse: CallHistoryResponse = {
          items: [],
          meta: {
            totalItems: 0,
            page: 1,
            totalPages: 0,
            itemCount: 0,
            limit: 10,
            nextPage: null,
            prevPage: null,
            offset: 10
          }
        };
        return emptyResponse;
      }
    } catch (error) {

      showToast(transformErrorResponse(error)?.message || "Failed to fetch calls", "error");
      throw transformErrorResponse(error);
    }
  },

  // Fetch single call by ID
  getCallById: async (callId: string) => {
    try {
      const result = await handleApiCall<ApiResponse<BackendCallRecordSingle>>(async () => {
        const response = await api.get<ApiResponse<BackendCallRecordSingle>>({
          url: getApiUrl(endpoint.communication.getCallById(callId)),
          auth: true
        });
        return response;
      });
      return result.data;
    } catch (error) {

      showToast(transformErrorResponse(error)?.message || "Failed to fetch call", "error");
      throw transformErrorResponse(error);
    }
  },

  // Fetch voicemails (hybrid: demo vs real)
  // Note: Voicemails are stored as call_histories with outcome='voicemail' in the database
  getVoicemails: async (): Promise<{ items: BackendCallRecord[] }> => {
    try {
      // Fetch voicemails from call_histories table with outcome='voicemail'
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: `${getApiUrl(endpoint.communication.getCalls)}?outcome=voicemail`,
          auth: true
        });
        return response;
      });

      // Type-safe extraction of items
      const typedResult = result as CallHistoryResponse | { data: CallHistoryResponse };
      const root = 'data' in typedResult ? typedResult.data : typedResult;
      const items = Array.isArray(root?.items) ? root.items : [];

      return { items };
    } catch (error) {
      const transformedError = transformErrorResponse(error);

      // Handle 404 as empty result instead of error for voicemails
      if (transformedError?.code === '404' || transformedError?.message?.includes('404')) {

        return { items: [] };
      }

      // For other errors, still show toast and return empty result
      showToast(transformedError?.message || "Failed to fetch voicemails", "error");
      return { items: [] };
    }
  },

  // Get call status by SID
  getCallStatus: async (callSid: string) => {
    try {
      const result = await handleApiCall<ApiResponse<CallStatusResponse>>(async () => {
        const response = await api.get<ApiResponse<CallStatusResponse>>({
          url: getApiUrl(endpoint.communication.getCallStatus(callSid)),
          auth: true
        });
        return response;
      });
      return result.data;
    } catch (error) {

      showToast(transformErrorResponse(error)?.message || "Failed to fetch call status", "error");
      throw transformErrorResponse(error);
    }
  },

  // Get SMS history
  getSms: async (params?: SmsHistoryQueryParams) => {
    try {
      const queryParams = new URLSearchParams();

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const base = getApiUrl(endpoint.communication.getSms || "/communication/sms");
      const url = params && Object.keys(params).length > 0
        ? `${base}?${queryParams.toString()}`
        : base;

      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: true
        });
        return response;
      });

      // Type-safe response handling
      if (result && Array.isArray((result as SmsHistoryResponse).items)) {
        return result as SmsHistoryResponse;
      } else {
        // Fallback: return empty structure if data is missing

        const emptyResponse: SmsHistoryResponse = {
          items: [],
          meta: {
            totalItems: 0,
            page: 1,
            totalPages: 0,
            itemCount: 0,
            limit: 10,
            nextPage: null,
            prevPage: null,
            offset: 10
          }
        };
        return emptyResponse;
      }
    } catch (error) {

      showToast(transformErrorResponse(error)?.message || "Failed to fetch SMS", "error");
      throw transformErrorResponse(error);
    }
  },

  // Mark voicemail as listened
  markVoicemailAsListened: async (voicemailId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.voicemail.markAsListened(voicemailId)),
          auth: true
        });
        return response;
      });
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message || "Failed to mark voicemail as listened", "error");
      throw transformErrorResponse(error);
    }
  },

  // Delete voicemail
  deleteVoicemail: async (voicemailId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.delete({
          url: getApiUrl(endpoint.voicemail.deleteVoicemail(voicemailId)),
          auth: true
        });
        return response;
      });
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message || "Failed to delete voicemail", "error");
      throw transformErrorResponse(error);
    }
  },

  // Get email history
  getEmails: async (params?: Record<string, string | number | undefined>) => {
    try {
      const queryParams = new URLSearchParams();

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const base = getApiUrl(endpoint.communication.getEmails || "/communication/emails");
      const url = params && Object.keys(params).length > 0
        ? `${base}?${queryParams.toString()}`
        : base;

      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: true
        });
        return response;
      });

      // Type-safe response handling for emails
      interface EmailResponse {
        items: unknown[];
        meta: PaginationMeta & { oauthConfigured?: boolean };
      }

      if (result && Array.isArray((result as EmailResponse).items)) {
        return result as EmailResponse;
      } else {

        const typedResult = result as EmailResponse | undefined;
        const emptyResponse: EmailResponse = {
          items: [],
          meta: {
            totalItems: 0,
            page: 1,
            totalPages: 0,
            itemCount: 0,
            limit: 10,
            nextPage: null,
            prevPage: null,
            offset: 10,
            oauthConfigured: typedResult?.meta?.oauthConfigured ?? true
          }
        };
        return emptyResponse;
      }
    } catch (error) {

      // Check if the error is due to OAuth not being configured
      // In that case, return empty result instead of throwing
      const errorResponse = transformErrorResponse(error);
      if (errorResponse?.message?.includes('Email integration not configured')) {
        return {
          items: [],
          meta: {
            totalItems: 0,
            page: 1,
            totalPages: 0,
            itemCount: 0,
            limit: 10,
            nextPage: null,
            prevPage: null,
            offset: 10,
            oauthConfigured: false
          }
        };
      }

      showToast(errorResponse?.message || "Failed to fetch emails", "error");
      throw errorResponse;
    }
  },

  // Sync emails for a specific lead from OAuth provider (Gmail/Microsoft)
  // This is triggered on-demand when user opens lead details or email history
  syncLeadEmails: async (leadId: string): Promise<{ imported: number; skipped: number; error?: string }> => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.oauth.syncLeadEmails(leadId)),
          auth: true
        });
        return response;
      });

      // Type-safe response handling
      const syncResult = result as { imported?: number; skipped?: number; message?: string };
      return {
        imported: syncResult?.imported ?? 0,
        skipped: syncResult?.skipped ?? 0,
      };
    } catch (error) {
      // Don't show toast for sync errors - it's a background operation
      // Just return error info for caller to handle
      const errorResponse = transformErrorResponse(error);
      return {
        imported: 0,
        skipped: 0,
        error: errorResponse?.message || "Failed to sync emails",
      };
    }
  },

  // Initiate race-to-answer session (calls multiple numbers, first to answer wins)
  initiateRaceToAnswer: async (
    phoneNumbers: Array<{ phone: string; leadId?: string; name?: string }>,
    fromPhoneNumber: string,
    metadata?: Record<string, unknown>
  ) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl('/api/communication/race-to-answer/initiate'),
          auth: true,
          body: {
            phoneNumbers,
            fromPhoneNumber,
            metadata,
          },
        });
        return response;
      });

      return result.data as {
        sessionId: string;
        callsInitiated: number;
        callSids: string[];
        conferenceName: string;
      };
    } catch (error) {
      showToast(transformErrorResponse(error)?.message || "Failed to initiate race-to-answer", "error");
      throw transformErrorResponse(error);
    }
  },
};

// Export the transformation function and types for use in components
export { transformCallRecord as transformCallData } from '../utils/transform-communication-data';

export type {
  BackendCallRecord as ApiCallRecord,
  BackendCallRecordSingle as ApiCallRecordSingle,
  CallHistoryQueryParams as CallsQueryParams,
  CallStatusResponse,
  CallHistoryItem,
  SmsHistoryItem,
} from '../types/communication-history.types';
